(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If AMD is available, return to the AMD Loader
    // and to the global object for compatibility.
    define(
      'searchBar',
      [
        'loadCss',
        'searchBarComponent',
        'searchBarResultHandler',
        'currentLocationComponent',
        'searchBarHelper'
      ],
      (loadCss, SearchBarComponent, SearchBarResultHandler, CurrentLocationComponent, SearchBarHelper) => {
        global.SearchBar = factory(
          global,
          loadCss,
          SearchBarComponent,
          SearchBarResultHandler,
          CurrentLocationComponent,
          SearchBarHelper
        );

        return global.SearchBar;
      }
    );
  } else if (typeof exports === 'object') {
    // If CommonJS is available, export the module
    // and attach it to the global object.
    global.SearchBar = factory(
      global,
      global.loadCss || (global.NewHomeSource && global.NewHomeSource.loadCss),
      global.NewHomeSource && global.NewHomeSource.SearchBarComponent,
      global.NewHomeSource && global.NewHomeSource.SearchBarResultHandler,
      global.NewHomeSource && global.NewHomeSource.CurrentLocationComponent,
      global.SearchBarHelper
    );
    module.exports = global.SearchBar;
  } else {
    // Otherwise, attach to global scope.
    global.SearchBar = factory(
      global,
      global.loadCss || (global.NewHomeSource && global.NewHomeSource.loadCss),
      global.NewHomeSource && global.NewHomeSource.SearchBarComponent,
      global.NewHomeSource && global.NewHomeSource.SearchBarResultHandler,
      global.NewHomeSource && global.NewHomeSource.CurrentLocationComponent,
      global.SearchBarHelper
    );
  }
}(
  typeof window !== 'undefined' ? window : {},
  (global, loadCss, SearchBarComponent, SearchBarResultHandler, CurrentLocationComponent, SearchBarHelper) => {
    'use strict';

    /**
     * searchBar.js provides functionality for searching communities, locations, builders, and articles.
     * It depends on the following components:
     * - loadCss: A utility to dynamically load CSS files for mobile devices.
     * - searchBarComponent: A component that represents the search bar UI. It is reusable.
     * - If we need to use this component in other places, we can directly use this component.
     * - searchBarResultHandler: A component that handles search results. It is dedicated to the search bar.
     * - currentLocationComponent: A component that displays the current location. It is reusable.
     * - If we need to use this component in other places, we can directly use this component.
     */

    global.NewHomeSource = global.NewHomeSource || {};
    const previousComponent = global.NewHomeSource.SearchBar;

    const searchBarInstances = [];
    const searchBarResultHandlerInstances = [];
    const currentLocationComponentInstances = [];
    const searchBarInputs = [];

    const smallViewportMediaQuery = window.matchMedia('(max-width: 1024px)');
    const pagesQuerySelectors = {
      home: '[data-home-search-bar]',
      details: '[data-details-container]'
    };

    /**
     * Checks whether the current page has a page-specific search bar selector.
     *
     * Some older pages, such as About Us, Luxury Homes, Condos, Townhouses,
     * and Master Planned Community, only support two viewport types: mobile and desktop.
     *
     * Those pages still depend on the old MVC/NhsRoute device detection flags.
     * In responsive mode, they may be treated as mobile even when the screen is
     * wider than 1024px.
     *
     * This JavaScript component uses 1024px as the breakpoint. Screens below
     * 1024px are treated as mobile/tablet, and screens 1024px or wider as desktop.
     *
     * To avoid breaking the search bar on older pages, we check for known
     * page-specific selectors. If any exist, the page is treated as an old page,
     * and the mobile search bar logic is not applied.
     *
     * Home and detail pages support three viewports: mobile, tablet, and desktop.
     * For those pages, this searchBar.js mobile logic applies below 1024px.
     *
     * @returns {boolean} True if any page-specific selector exists; otherwise false.
     */

    function doesAnyPageQuerySelectorExist() {
      return Object.values(pagesQuerySelectors).some((selector) => !!document.querySelector(selector));
    }

    /**
     * Checks if the current device is mobile based on a specific query selector.
     * This is used as an additional check alongside the viewport size to determine if we should apply
     * mobile-specific search bar logic.
     * @returns {boolean} True if the device is identified as mobile, false otherwise.
     */
    function checkIsMobileFromQuerySelector() {
      if (doesAnyPageQuerySelectorExist()) {
        return false;
      }

      const isMobileDeviceElement = document.querySelector('[data-ismobiledevice]');

      if (!isMobileDeviceElement) {
        return false;
      }

      const isMobileDeviceValue = isMobileDeviceElement.getAttribute('data-ismobiledevice') || '';

      return isMobileDeviceValue.toLowerCase() === 'true';
    }

    /**
     * Checks if the current device is a small viewport.
     * This function uses both a media query and a specific query selector to determine if the device
     * should be treated as mobile for the search bar functionality.
     * @returns {boolean} True if the viewport is small, false otherwise.
     */
    function isSmallViewport() {
      if (checkIsMobileFromQuerySelector()) {
        return true;
      }

      return smallViewportMediaQuery.matches;
    }

    const searchBarState = {
      searchText: null,
      searchBarContainer: null,
      isMobile: isSmallViewport()
    };

    /**
     * Updates the internal state of the search bar.
     * @param {Object} state - The new state to set.
     */
    function updateSearchBarState(state) {
      searchBarState.searchText = state && state.searchText ? state.searchText.trim() : null;
      searchBarState.searchBarContainer = state && state.searchBarContainer ? state.searchBarContainer : null;
      searchBarState.isMobile = isSmallViewport();
    }

    /**
     * Reads the last searched text from a cookie.
     * @returns {string|null} The last searched text or null if not found.
     */
    function readLastSearchCookie() {
      const cookieData = `; ${document.cookie}`;
      const parts = cookieData.split(`; ${'Personal_1'}=`);
      const lastSearchedText = parts.pop().split(';').shift();

      const searchText = lastSearchedText
        ? new URLSearchParams(lastSearchedText).get('searchtext') || null
        : null;

      return searchText;
    }

    // Keep track of the current AbortController for fetch requests
    let currentController = null;

    /**
     * Fetches search results from the server.
     * @param {string} searchText - The text to search for.
     * @returns {Promise<Object|null>} A promise that resolves to the search results or null on failure.
     *
     * This API call is intentionally kept within this file to maintain simplicity,
     * based on discussions with front-end experts.
     * If the logic becomes more complex in the future,
     * please refactor and move it to a dedicated service.
     */
    function fetchSearchResults(searchText) {
      if (!searchText.trim()) {
        return Promise.resolve(null);
      }

      // Cancel previous fetch if it exists
      if (currentController) {
        currentController.abort();
      }

      currentController = new AbortController();
      const signal = currentController.signal;

      return fetch('/getsearchbarresults?searchterm=' + encodeURIComponent(searchText), { signal })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null) // silently ignore all errors including AbortError
        .finally(() => {
          if (currentController === signal) {
            currentController = null;
          }
        });
    }

    /**
     * Checks if the provided text has any non-whitespace characters.
     * @param {string} text - The text to check.
     * @returns {boolean} True if there is text, false otherwise.
     */
    function hasSearchText(text) {
      return text && text.trim().length > 0;
    }

    /**
     * Triggers the current location functionality when search text is empty.
     * Calls the CurrentLocationComponent's handleCurrentLocationClick method if the component is available.
     */
    function displayCurrentLocationIfCookieEmpty() {
      if (CurrentLocationComponent) {
        CurrentLocationComponent.handleCurrentLocationClick();
      }
    }

    /**
     * Handles the submit button click event.
     */
    function handleSubmitButtonClickEvent() {
      const searchText = readLastSearchCookie() || '';

      if (!hasSearchText(searchText)) {
        displayCurrentLocationIfCookieEmpty();
        return;
      }

      fetchSearchResults(searchText).then((response) => {
        if (response && response.Results && response.Results.length > 0) {
          window.location.href = response.Results[0].LocationUrl;
        }
      });
    }

    /**
     * Handles the Enter key event on the search input.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    function handleEnterKey(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmitButtonClickEvent();
      }
    }

    /**
     * Destroys all instances of SearchBarComponent, SearchBarResultHandler, and CurrentLocationComponent.
     */
    function destroyInstances() {
      if (Array.isArray(searchBarInstances)) {
        while (searchBarInstances.length > 0) {
          const instance = searchBarInstances.pop();
          if (instance && typeof instance.destroyEvents === 'function') {
            instance.destroyEvents();
          }
        }
      }

      if (Array.isArray(searchBarResultHandlerInstances)) {
        while (searchBarResultHandlerInstances.length > 0) {
          const instance = searchBarResultHandlerInstances.pop();
          if (instance && typeof instance.destroyInstances === 'function') {
            instance.destroyInstances();
          }
        }
      }

      if (Array.isArray(currentLocationComponentInstances)) {
        while (currentLocationComponentInstances.length > 0) {
          const instance = currentLocationComponentInstances.pop();
          if (instance && typeof instance.destroyInstances === 'function') {
            instance.destroyInstances();
          }
        }
      }

      if (Array.isArray(searchBarInputs)) {
        while (searchBarInputs.length > 0) {
          const input = searchBarInputs.pop();
          if (input && typeof input.removeEventListener === 'function') {
            input.removeEventListener('keydown', handleEnterKey);
          }
        }
      }
    }

    /**
     * Gets the modal element for the search bar.
     * @returns {HTMLElement|null} The modal element or null if not found.
     */
    function getModalElement() {
      return document.querySelector('[data-search-bar-modal]');
    }

    /**
     * Show or hide the search results message container based on search text and device type.
     */
    function toggleSearchResultsMessageContainer(forceHide = false) {
      if (isSmallViewport()) {
        const modal = getModalElement();
        if (!modal) {
          return;
        }

        const container = modal.querySelector('[data-search-results-info]');
        if (container) {
          container.hidden = forceHide || (searchBarState.searchText || '').trim().length > 0;

          const searchKeywordContainer = container.querySelector('[data-search-keyword-container]');
          searchKeywordContainer.hidden = true;
        }
      }
    }

    /**
     * Show or hide a list of elements.
     * @param {HTMLElement[]} elements - Array of elements
     * @param {boolean} shouldShow - Whether to show (true) or hide (false)
     */
    function setElementsVisibility(elements, shouldShow) {
      elements.forEach((element) => {
        if (element) {
          const uiElement = element;
          uiElement.hidden = !shouldShow;
        }
      });
    }

    /**
     * Filters the provided desktop bars to include only those that contain a submit button.
     * This ensures that only functional desktop search bars are displayed.
     * @param {HTMLElement[]} desktopBars - Array of desktop search bar elements to filter.
     * @returns {HTMLElement[]} Filtered array of desktop search bar elements that contain a submit button.
     */
    function getDesktopBarsWithSubmitButton(desktopBars) {
      if (!desktopBars) {
        return [];
      }

      return Array.from(desktopBars).filter(
        (desktopBar) => desktopBar.querySelector('[data-search-bar-submit-button]')
      );
    }

    /**
     * Filters the provided desktop bars to include only those that do not contain a submit button.
     * This is used to hide any desktop search bars that are not functional
     * (i.e., those without a submit button).
     * @param {HTMLElement[]} desktopBars - Array of desktop search bar elements to filter.
     * @returns {HTMLElement[]} Filtered array of desktop search bar elements that do not
     * contain a submit button.
     */
    function getDesktopBarsWithoutSubmitButton(desktopBars) {
      if (!desktopBars) {
        return [];
      }

      return Array.from(desktopBars).filter(
        (desktopBar) => !desktopBar.querySelector('[data-search-bar-submit-button]')
      );
    }

    /**
     * Core logic to determine which bars to show/hide based on device type.
     * @param {HTMLElement[]} desktopBars
     * @param {boolean} isMobileViewport - True if device is mobile
     */
    function toggleBars(desktopBars, isMobileViewport) {
      const showDesktopBars = !isMobileViewport;
      const desktopBarsWithSubmitButton = getDesktopBarsWithSubmitButton(desktopBars);
      const desktopBarsWithoutSubmitButton = getDesktopBarsWithoutSubmitButton(desktopBars);

      if (isMobileViewport) {
        // On mobile, show desktop bars only if they have a submit button (are functional)
        setElementsVisibility(desktopBarsWithSubmitButton, true);
        setElementsVisibility(desktopBarsWithoutSubmitButton, false);
      } else {
        // On desktop, show all desktop bars
        setElementsVisibility(desktopBarsWithSubmitButton, showDesktopBars);
        setElementsVisibility(desktopBarsWithoutSubmitButton, showDesktopBars);
      }
    }

    /**
     * Toggles the visibility of search bars based on the device type.
     */
    function toggleSearchBarsForDevice() {
      const desktopSearchBars = document.querySelectorAll('[data-search-bar-container-desktop]');
      toggleBars(desktopSearchBars, isSmallViewport());
    }

    /**
     * Loads the CSS for the modal.
     */
    function loadModalCss() {
      const cssBundle = document.querySelector('#searchPanelExpanded');
      const cssLoader = loadCss || (global.NHS && global.NHS.loadCss);

      if (cssBundle && typeof cssLoader === 'function') {
        cssLoader(cssBundle.value);
      }
    }

    /**
     * Debounces a function that returns a promise.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The debounce delay in milliseconds.
     */
    function debouncePromise(func, wait) {
      let timeout;
      let lastCall = null;

      return (...args) => {
        clearTimeout(timeout);

        return new Promise((resolve, reject) => {
          lastCall = { args, resolve, reject };
          timeout = setTimeout(() => {
            timeout = null;
            const current = lastCall;
            lastCall = null;
            func(...current.args).then(current.resolve).catch(current.reject);
          }, wait);
        });
      };
    }

    /**
     * Subscribes to location selection changes from the SearchBarResultHandler instance.
     * @param {SearchBarResultHandler} instance - The SearchBarResultHandler instance.
     * @param {Function|null} initializeSearchBarComponentsCallback
     * Optional callback to initialize search bar components.
     */
    function subscribeSelectLocationChanges(instance, initializeSearchBarComponentsCallback) {
      instance.subscribeSelectLocationChange((location) => {
        updateSearchBarState({
          searchText: location,
          searchBarContainer: searchBarState.searchBarContainer
        });

        if (initializeSearchBarComponentsCallback) {
          initializeSearchBarComponentsCallback(false);
        }
      });
    }

    let searchResults = [];

    /**
     * Displays search results using SearchBarResultHandler.
     * @param {Function|null} initializeSearchBarComponentsCallback
     * Optional callback to initialize search bar components.
     */
    function displaySearchResults(initializeSearchBarComponentsCallback) {
      const config = {
        searchText: searchBarState.searchText,
        searchBarContainer: searchBarState.searchBarContainer,
        isMobile: searchBarState.isMobile,
        searchResults
      };

      const instance = new SearchBarResultHandler(config);
      searchBarResultHandlerInstances.push(instance);
      subscribeSelectLocationChanges(instance, initializeSearchBarComponentsCallback);
    }

    /**
     * Fetches search results and renders them using SearchBarResultHandler.
     * @param {string} searchText - The text to search for.
     * @param {Function|null} initializeSearchBarComponentsCallback
     * Optional callback to initialize search bar components.
     */
    function fetchResultsAndRenderSearchResult(searchText, initializeSearchBarComponentsCallback) {
      const debouncedFetchSearchResults = debouncePromise(fetchSearchResults, 300);
      debouncedFetchSearchResults(searchText).then((response) => {
        if (response && response.Results && response.Results.length > 0) {
          searchResults = response.Results;
        }
      }).finally(() => {
        displaySearchResults(initializeSearchBarComponentsCallback);
      });
    }

    /**
     * Binds the Enter key event to the search input element
     * and returns a cleanup function to remove it later.
     * @param {HTMLElement} inputElement - The search input element.
     * @returns {Function|null} Cleanup function to remove the event listener.
     */
    function bindSearchInputEnterKeyEvent(inputElement) {
      if (!inputElement) {
        return;
      }

      inputElement.addEventListener('keydown', handleEnterKey);
      searchBarInputs.push(inputElement);
    }

    /**
     * Subscribes to the open status changes of the CurrentLocationComponent instance.
     * @param {CurrentLocationComponent} instance - The CurrentLocationComponent instance.
     */
    function subscribeToCurrentLocationOpenStatus(instance) {
      instance.subscribeOpenStatus((isOpen) => {
        toggleSearchResultsMessageContainer(isOpen);
      });
    }

    /**
     * Displays the current location component.
     */
    function displayCurrentLocation() {
      const currentLocationComponent = new CurrentLocationComponent({
        searchBarState
      });

      currentLocationComponentInstances.push(currentLocationComponent);

      if (isSmallViewport()) {
        subscribeToCurrentLocationOpenStatus(currentLocationComponent);
      }
    }

    /**
     * Displays the last searched text in the mobile home.
     * It has not search functionality, just display purpose.
     * @param {string} searchText - The text to display.
     */
    function displayLastSearchTextHomeMobile(searchText) {
      if (isSmallViewport()) {
        const inputReadonlyElement = document.querySelector('[data-home-mobile-search-input]');
        if (inputReadonlyElement) {
          inputReadonlyElement.value = searchText;
        }
      }
    }

    /**
     * Handles the search bar event.
     * @param {string} searchText - The text entered in the search bar.
     * @param {string} searchBarContainer - The container element for the search bar.
     * @param {Function|null} initializeSearchBarComponentsCallback
     * Optional callback to initialize search bar components.
     */
    function handleSearchBarInputUpdateEvent(searchText, searchBarContainer, initializeSearchBarComponentsCallback) {
      updateSearchBarState({
        searchText,
        searchBarContainer
      });

      displayCurrentLocation();
      toggleSearchResultsMessageContainer();

      if (hasSearchText(searchText)) {
        fetchResultsAndRenderSearchResult(searchText, initializeSearchBarComponentsCallback);
      } else {
        searchResults = [];
        displaySearchResults(initializeSearchBarComponentsCallback);
      }
    }

    /**
     * Handles the current location when the search input is focused.
     * @param {string} container - The container element for the search bar.
     */
    function handleCurrentLocation(container, value) {
      updateSearchBarState({
        searchText: value,
        searchBarContainer: container
      });

      displayCurrentLocation();
    }

    /**
     * Subscribes to events from the SearchBarComponent instance.
     * @param {SearchBarComponent} instance - The SearchBarComponent instance.
     * @param {Function|null} initializeSearchBarComponentsCallback
     * Optional callback to initialize search bar components.
     */
    function subscribeToSearchBarComponentEvents(instance, initializeSearchBarComponentsCallback) {
      instance.getInputChangeObservable().subscribe(({ container, value }) => {
        handleSearchBarInputUpdateEvent(value, container, initializeSearchBarComponentsCallback);
        displayLastSearchTextHomeMobile(value);
      });

      instance.getSubmitButtonClickObservable().subscribe(() => {
        handleSubmitButtonClickEvent();
      });

      instance.getInputFocusObservable().subscribe(({ container, value }) => {
        handleCurrentLocation(container, value);
      });
    }

    /**
     * Updates the search results message container visibility.
     */
    function updateSearchResultsMessageContainer() {
      toggleSearchResultsMessageContainer();
    }

    /**
     * Creates a configuration object for UI visibility management.
     * @returns {Object} The configuration object.
     */
    function createUIVisibilityConfig() {
      const homeSearchBar = document.querySelector('[data-home-search-bar]');
      const detailsContainer = document.querySelector('[data-details-container]');
      const isBasicDetailsPage = document.querySelector('[data-is-basic-page]');
      const header = document.querySelector('[data-main-header]');
      const sideMenu = document.querySelector('[data-menu-side]');
      const modal = getModalElement();

      return {
        modal,
        homeSearchBar,
        detailsContainer,
        isBasicDetailsPage,
        header,
        sideMenu,
        loadModalCss
      };
    }

    /**
     * Sets the visibility of the search bar modal.
     * @param {boolean} shouldShow - Whether to show the modal.
     */
    function setModalVisibility(shouldShow) {
      const modal = getModalElement();
      if (modal) {
        modal.hidden = !shouldShow;
      }
    }

    /**
     * Toggles the mobile search bar modal.
     * @param {boolean|null} forceOpen - If true, forces the modal to open;
     * if false, forces it to close; if null, toggles the current state.
     */
    function toggleMobileSearchBarModal(forceOpen = null) {
      const modal = getModalElement();
      if (!modal) {
        return;
      }

      updateSearchResultsMessageContainer();

      const isModalOpen = !modal.hidden;
      const shouldOpenModal = forceOpen === null ? !isModalOpen : forceOpen;

      const config = createUIVisibilityConfig();
      SearchBarHelper.updateLayoutVisibility(shouldOpenModal, config);
      setModalVisibility(shouldOpenModal);
      SearchBarHelper.toggleScroll(document.body, shouldOpenModal);
    }

    let mobileSearchEventsBound = false;

    /**
     * Binds events for mobile search functionality.
     */
    function bindMobileEvents() {
      if (!mobileSearchEventsBound) {
        const mobileMenuButton = document.querySelector('[data-open-search-bar-modal]');
        const closeButton = document.querySelector('[data-close-search-bar-modal]');

        if (mobileMenuButton) {
          mobileMenuButton.addEventListener('click', () => toggleMobileSearchBarModal(true));
        }

        if (closeButton) {
          closeButton.addEventListener('click', () => toggleMobileSearchBarModal(false));
        }

        // Mark as bound only after attempting registrations (prevents duplicate listeners)
        mobileSearchEventsBound = true;
      }
    }

    /**
     * Gets the last search text from the state or cookie.
     * @returns {string|null} The last search text.
     */
    function getLastSearchText() {
      return hasSearchText(searchBarState.searchText)
        ? searchBarState.searchText
        : readLastSearchCookie();
    }

    function toggleSearchBarOutsideModalAttribue(elements, shouldAddAttribute) {
      elements.forEach((element) => {
        if (element) {
          element.toggleAttribute('data-mobile-search-bar-outside-modal', shouldAddAttribute);
        }
      });
    }

    /**
     * Initializes the search bar components.
     * @param {boolean} cleanupUnusedInstances - Whether to clean up unused instances.
     */
    function initializeSearchBarComponents(cleanupUnusedInstances = true) {
      if (cleanupUnusedInstances) {
        destroyInstances();
      }

      const searchBarContainerElements = document.querySelectorAll('[data-search-bar-container-desktop]');
      const searchBarContainerElementsArray = Array.from(searchBarContainerElements);
      toggleSearchBarsForDevice();
      toggleSearchBarOutsideModalAttribue(searchBarContainerElementsArray, isSmallViewport());

      if (isSmallViewport()) {
        bindMobileEvents();
        const modalElement = getModalElement();
        if (!modalElement) {
          return;
        }

        const smallScreenContainerElements = modalElement.querySelector('[data-search-bar-container-mobile]');
        const smallScreenSearchBarElement = modalElement ? smallScreenContainerElements : null;

        if (smallScreenSearchBarElement) {
          searchBarContainerElementsArray.push(smallScreenSearchBarElement);
        }

        displayLastSearchTextHomeMobile(getLastSearchText() || '');
        loadModalCss();
      }

      const lastSearchText = getLastSearchText() || '';

      const lastSearchFlag = document.querySelector('[data-suppress-last-search]');
      const suppressLastSearch = !!(lastSearchFlag && lastSearchFlag.value === 'true');

      searchBarContainerElementsArray.forEach((searchBarContainer) => {
        const searchBarSubmitButton = searchBarContainer.querySelector('[data-search-bar-submit-button]');

        const config = {
          searchBarContainer,
          searchInput: searchBarContainer.querySelector('[data-search-input]'),
          resetButton: searchBarContainer.querySelector('[data-search-input-reset-button]'),
          submitButton: searchBarSubmitButton || document.querySelector('[data-search-bar-submit-button]'),
          searchBarInputHighlightContainer: searchBarContainer.querySelector('[data-search-bar-input-container]'),
          lastSearchedText: suppressLastSearch ? '' : lastSearchText
        };

        updateSearchBarState({
          searchText: config.lastSearchedText,
          searchBarContainer: config.searchBarContainer
        });

        const instance = new SearchBarComponent(config);
        searchBarInstances.push(instance);

        subscribeToSearchBarComponentEvents(instance, initializeSearchBarComponents);
        bindSearchInputEnterKeyEvent(config.searchInput);
      });
    }

    let viewportChangeDebounceTimer;

    /**
     * Handles changes in the viewport size and re-initializes the search bar components
     * if the viewport type has changed (e.g., from mobile to desktop or vice versa).
     * This function is debounced to prevent excessive calls during rapid viewport resizing.
     * Prevents unnecessary re-initialization if the viewport type has not actually changed.
     */
    function handleViewportChange(e) {
      const debounceDelay = 150;
      clearTimeout(viewportChangeDebounceTimer);

      viewportChangeDebounceTimer = setTimeout(() => {
        const isMobile = e.matches;
        if (searchBarState.isMobile === isMobile) {
          return;
        }

        searchBarState.isMobile = isMobile;
        initializeSearchBarComponents(true);
      }, debounceDelay);
    }

    // Initialize search bar component
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeSearchBarComponents);
    } else {
      initializeSearchBarComponents();
    }

    // Re-initialize when viewport changes
    smallViewportMediaQuery.addEventListener('change', handleViewportChange);

    /**
     * API for the SearchBar component
     */
    const api = {
      destroyInstances,
      noConflict() {
        global.NewHomeSource.SearchBar = previousComponent;
        return api;
      }
    };

    global.NewHomeSource.SearchBar = api;
    return api;
  }
));
